'use client';
import React, { ReactElement, useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { BiSolidRocket, BiSolidStar, BiStar } from 'react-icons/bi';
import LouiseButton, { CtaTargetType } from '@/components/buttons/LouiseButton';
import LouiseWatchNowButton, { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import { useModal } from '@/components/ModalContext/ModalContext';
import { IconType } from 'react-icons';

type IBenefits = {
  icon: ReactElement;
  text: string;
};

type IStat = {
  value: string;
  label: string;
};

export type IImageOrientation = 'portrait' | 'landscape' | 'square';

type HeroContent = {
  badge: string;
  title: string;
  description: string;
  cta: string;
  ctaTarget?: CtaTargetType;
  btnDemoText?: string;
  btnDemoTarget?: WatchButtonCtaTargetType;
  trustedBy?: string;
  img: string;
  icon: IconType;
  imgOrientation?: IImageOrientation;
};

type HeroBotsProps = {
  benefits: IBenefits[];
  stats: IStat[];
  content: HeroContent;
  locale: string;
};

export function HeroBots({ benefits, stats, content, locale }: HeroBotsProps) {
  const { openModal } = useModal();
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  let imageWidth: number, imageHeight: number;

  switch (content.imgOrientation) {
    case 'portrait':
      imageWidth = 315;
      imageHeight = 560;
      break;
    case 'landscape':
      imageWidth = 560;
      imageHeight = 315;
      break;
    case 'square':
      imageWidth = 400;
      imageHeight = 400;
      break;
    default:
      imageWidth = 315;
      imageHeight = 560;
      break;
  }

  const handleOpenDemoModal = (btnDemoTarget: string) => {
    switch (btnDemoTarget) {
      case 'discover-chatbot-ai':
        openModal('DISCOVER_CHATBOT_AI', {});
        return;
      case 'discover-chatbot-text':
        openModal('DISCOVER_CHATBOT_TEXT', {});
        return;
      case 'discover-videobot':
        openModal('DISCOVER_VIDEOBOT', {});
        return;
      case 'discover-human-clone-link':
        openModal('DISCOVER_HUMAN_CLONE_LINK', {});
        return;
      default:
        return;
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePosition({ x: 0, y: 0 });
  };

  // Calculer la rotation basée sur la position de la souris
  const getCardTransform = () => {
    if (!cardRef.current || !isHovered) return '';

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((mousePosition.y - centerY) / centerY) * -10;
    const rotateY = ((mousePosition.x - centerX) / centerX) * 10;

    return `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  // Calculer l'angle du reflet basé sur l'inclinaison de la carte
  const getReflectionAngle = () => {
    if (!cardRef.current || !isHovered) return 45;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateY = ((mousePosition.x - centerX) / centerX) * 10;
    const rotateX = ((mousePosition.y - centerY) / centerY) * -10;

    // Calculer l'angle du reflet en fonction de l'inclinaison
    return 45 + rotateY * 2 + rotateX;
  };

  const reflectionAngle = getReflectionAngle();

  return (
    <section className="tw-relative tw-pb-8 tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-900 tw-via-blue-900 tw-to-purple-900 tw-overflow-hidden">
      {/* Background Effects */}
      <div className="tw-absolute tw-inset-0">
        <div className="tw-absolute tw-top-1/4 tw-left-1/4 tw-w-96 tw-h-96 tw-bg-blue-500/20 tw-rounded-full tw-blur-3xl tw-animate-pulse"></div>
        <div className="tw-absolute tw-bottom-1/4 tw-right-1/4 tw-w-80 tw-h-80 tw-bg-purple-500/20 tw-rounded-full tw-blur-3xl tw-animate-pulse"></div>
        <div
          className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-64 tw-h-64 tw-bg-gradient-to-r tw-from-blue-400/10 tw-to-purple-400/10 tw-rounded-full tw-blur-2xl tw-animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>

      <div className="tw-relative tw-z-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        <div className="tw-grid lg:tw-grid-cols-2 tw-gap-12 tw-items-center tw-min-h-screen tw-py-12">
          {/* Content */}
          <div className="tw-space-y-8 lg:tw-space-y-10">
            {/* Badge */}
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100/10 tw-backdrop-blur-sm tw-text-blue-300 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-border-blue-400/20 tw-shadow-lg">
              <content.icon className="tw-w-4 tw-h-4" />
              {content.badge}
            </div>

            {/* Title */}
            <h1 className="tw-text-4xl sm:tw-text-5xl lg:tw-text-6xl tw-font-bold tw-text-white tw-leading-tight">
              {content.title}
            </h1>

            {/* Description */}
            <p className="tw-text-lg sm:tw-text-xl tw-text-gray-300 tw-leading-relaxed tw-max-w-2xl">
              {content.description}
            </p>

            {/* Benefits */}
            <div className="tw-flex tw-flex-wrap tw-gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="tw-flex tw-items-center tw-gap-3 tw-text-green-400 tw-bg-green-400/10 tw-px-4 tw-py-2 tw-rounded-full tw-backdrop-blur-sm"
                >
                  {benefit.icon}
                  <span className="tw-font-medium tw-text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>
            {/* CTA */}
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-center">
              <LouiseButton
                color="blue"
                cta={content.cta}
                ctaTarget={content.ctaTarget as CtaTargetType}
                icon={<BiSolidRocket className="tw-w-5 tw-h-5" />}
                gradiant={true}
                locale={locale}
              />

              {content.btnDemoTarget && content.btnDemoText && (
                <LouiseWatchNowButton
                  textColor="white"
                  cta={content.btnDemoText}
                  ctaTarget={content.btnDemoTarget as WatchButtonCtaTargetType}
                  locale={locale}
                  onClick={() => handleOpenDemoModal(content.btnDemoTarget as string)}
                />
              )}
            </div>
            {/* Trust indicators */}
            {content.trustedBy && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-pt-8 tw-border-t tw-border-white/10">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-flex">
                    {[...Array(5)].map((_, i) => (
                      <BiSolidStar key={i} className="tw-w-5 tw-h-5 tw-text-yellow-400 tw-fill-current" />
                    ))}
                  </div>
                  <span className="tw-text-white tw-font-semibold tw-text-sm">4.8/5</span>
                </div>
                <span className="tw-text-gray-400 tw-text-sm">{content.trustedBy}</span>
              </div>
            )}

            {/* Stats */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-6 tw-pt-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="tw-text-center tw-bg-white/5 tw-backdrop-blur-sm tw-rounded-lg tw-p-4 tw-border tw-border-white/10"
                >
                  <div className="tw-text-2xl tw-font-bold tw-text-white tw-mb-1">{stat.value}</div>
                  <div className="tw-text-gray-400 tw-text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image avec effet de carte 3D */}
          <div className="tw-relative tw-flex tw-justify-center lg:tw-justify-end">
            <div
              ref={cardRef}
              className="tw-relative tw-group tw-cursor-pointer"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{
                transform: getCardTransform(),
                transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.3s ease-out',
              }}
            >
              {/* Conteneur de l'image avec effet de reflet */}
              <div className="tw-relative tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-white/5 tw-to-transparent tw-backdrop-blur-sm tw-border tw-border-white/10 tw-shadow-2xl lg:tw-block tw-hidden">
                <Image
                  className="tw-max-w-full tw-h-auto tw-transition-transform tw-duration-300"
                  priority
                  quality={100}
                  alt={content.title}
                  src={content.img}
                  width={imageWidth}
                  height={imageHeight}
                />

                {/* Effet de reflet naturel qui suit l'inclinaison de la carte */}
                <div
                  className="tw-absolute tw-inset-0 tw-pointer-events-none tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-50"
                  style={{
                    background: `linear-gradient(${reflectionAngle}deg, 
                      transparent 0%, 
                      rgba(255,255,255,0.1) 45%, 
                      rgba(255,255,255,0.3) 50%, 
                      rgba(255,255,255,0.1) 55%, 
                      transparent 100%)`,
                    transition: 'background 0.1s ease-out, opacity 0.3s ease-out',
                  }}
                />

                {/* Reflet subtil permanent */}
                <div
                  className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-pointer-events-none tw-opacity-20"
                  style={{
                    background: `linear-gradient(135deg, 
                      transparent 0%, 
                      rgba(255,255,255,0.1) 30%, 
                      rgba(255,255,255,0.2) 40%, 
                      rgba(255,255,255,0.1) 50%, 
                      transparent 100%)`,
                  }}
                />

                {/* Effet de bordure lumineuse */}
                <div className="tw-absolute tw-inset-0 tw-rounded-3xl tw-border tw-border-transparent tw-transition-all tw-duration-300 group-hover:tw-border-white/20" />
              </div>

              {/* Ombre portée dynamique */}
              <div
                className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-500/10 tw-to-purple-500/10 tw-rounded-3xl tw-blur-2xl tw--z-10 tw-transition-all tw-duration-300"
                style={{
                  transform: isHovered ? 'translateY(10px) scale(1.05)' : 'translateY(20px)',
                  opacity: isHovered ? 0.6 : 0.3,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="tw-hidden sm:tw-block tw-absolute tw-bottom-8  tw-left-1/2 tw-transform tw--translate-x-1/2 tw-animate-bounce">
        <div className="tw-w-6 tw-h-10 tw-border-2 tw-border-white/30 tw-rounded-full tw-flex tw-justify-center">
          <div className="tw-w-1 tw-h-3 tw-bg-white/50 tw-rounded-full tw-mt-2 tw-animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
